import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Select from 'react-select';
import { itemsService } from '../../services/items.service';
import { GetItemList, SetTItemList, SettItemBackupList } from '../../store/itemAtom';

// Professional, clean react-select styles (Tailwind-like)
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 48,
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#3B82F6' : '#D1D5DB',
    boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
    '&:hover': { borderColor: '#9CA3AF' },
  }),
  menu: (base) => ({
    ...base,
    marginTop: 8,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
    // zIndex: 9999,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#2563EB' : state.isFocused ? '#EFF6FF' : 'white',
    color: state.isSelected ? 'white' : '#1F2937',
    padding: '12px 16px',
    fontSize: '14px',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#1F2937',
    fontWeight: 500,
  }),
  placeholder: (base) => ({
    ...base,
    color: '#9CA3AF',
  }),
};

function StockAdjustmentItemSelect({ onOptionSelect, cleared = false, placeholder = "Search and select an item..." }) {
  const [selected, setSelected] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const itemAtomData = GetItemList();
  const setItemListState = SetTItemList();
  const setItemBackupList = SettItemBackupList();

  // Load items from server if not already loaded
  const loadItemsFromServer = useCallback(() => {
    if (itemAtomData?.item_info?.length > 0) return;

    setIsLoading(true);
    itemsService
      .getItemsList()
      .then((res) => {
        if (res?.data?.item_info?.length > 0) {
          setItemListState(res.data);
          setItemBackupList(res.data);
        }
      })
      .catch((err) => {
        console.error('Failed to load items:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [itemAtomData?.item_info?.length, setItemListState, setItemBackupList]);

  useEffect(() => {
    loadItemsFromServer();
  }, [loadItemsFromServer]);

  // Clear selection when parent says so (e.g., after adding item)
  useEffect(() => {
    if (cleared) {
      setSelected(null);
      setSearchInput('');
    }
  }, [cleared]);

  // Transform atom data into react-select format
  const options = useMemo(() => {
    if (!itemAtomData?.item_info || !Array.isArray(itemAtomData.item_info)) return [];

    return itemAtomData.item_info.map((item) => ({
      value: item.Item_Id,
      label: item.Item_Name,
      Item_Id: item.Item_Id,
      Item_Name: item.Item_Name,
      Item_Sku: item.Item_Sku || '',
      Category_Name: item.Item_Category_Name || 'Uncategorized',
    }));
  }, [itemAtomData?.item_info]);

  // Client-side search filter
  const filteredOptions = useMemo(() => {
    if (!searchInput.trim()) return options;

    const term = searchInput.toLowerCase();
    return options.filter((option) => {
      return (
        option.label.toLowerCase().includes(term) ||
        option.Item_Sku.toLowerCase().includes(term) ||
        option.value.toString().includes(term)
      );
    });
  }, [options, searchInput]);

  // Beautiful option with SKU and Category
  const formatOptionLabel = ({ label, Item_Sku, Category_Name }) => (
    <div className="flex flex-col">
      <div className="font-medium text-gray-900">{label}</div>
      <div className="text-xs text-gray-500">
        {Item_Sku && `${Item_Sku}`} {Category_Name && Item_Sku && ' • '}
        {Category_Name}
      </div>
    </div>
  );

  const handleChange = (newValue) => {
    setSelected(newValue);
    onOptionSelect(newValue); // Pass full item object or null
  };

  return (
    <Select
      id="stock-adjustment-item-select"
      instanceId="stock-adjustment-item-select"
      value={selected}
      onChange={handleChange}
      onInputChange={setSearchInput}
      options={filteredOptions}
      formatOptionLabel={formatOptionLabel}
      placeholder={
        isLoading ? 'Loading items...' : placeholder
      }
      isLoading={isLoading}
      isClearable
      isSearchable
      filterOption={() => true} // We handle filtering
      noOptionsMessage={({ inputValue }) =>
        inputValue ? 'No items found' : 'No items available'
      }
      styles={selectStyles}
      menuPlacement="auto"
      loadingMessage={() => 'Loading items...'}
      components={{
        DropdownIndicator: () => (
          <div className="pr-3">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13l-8 8-8-8" />
            </svg>
          </div>
        ),
        LoadingIndicator: () => (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
        ),
      }}
    />
  );
}

export default StockAdjustmentItemSelect;